import { useFormik } from "formik";
import {
  HotelBookingSummary,
  type SummaryFormValues,
} from "./HotelBookingSummary";
import * as yup from "yup";
import { type HotelBooking } from "@/app/lib/types/types";
import type {
  FindHotelsOutputData,
  FindUserTravelersTravelers,
} from "@/app/lib/types/codegen";
import BottomDrawer from "../flight-purchase/BottomDrawer";
import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import { formatCurrency } from "@/app/lib/utils/currency";
import {
  includeBnplFee,
  calculateMonthlyInstallment,
} from "@/app/lib/utils/bnpl-fee";
import { getTravellerTypeAtTravelDate } from "@/app/lib/utils/ageValidation";

export interface HotelBookingSummaryFormProps {
  booking: HotelBooking;
  travelDate: Date;
  hotel: FindHotelsOutputData;
  loading: boolean;
  disabledSubmit?: boolean;
  rooms?: { adults: number; children: number }[];
  buttonLabel: string;
  selectedTravellers: FindUserTravelersTravelers[];
  handleSubmit: (values: SummaryFormValues) => void;
}

export const HotelBookingSummaryForm = ({
  booking,
  travelDate,
  hotel,
  loading,
  rooms = [],
  buttonLabel,
  disabledSubmit,
  selectedTravellers,
  handleSubmit,
}: HotelBookingSummaryFormProps) => {
  const { paymentType, selectedInstallments } = usePaymentContext();

  const hotelTotalPrice =
    booking?.rooms.reduce((acc, room) => acc + (room.price?.value || 0), 0) ||
    0;

  // Calculate BNPL fee if BNPL is selected
  const includedBnplFee = includeBnplFee(hotelTotalPrice);
  const totalPrice = paymentType === "bnpl" ? includedBnplFee : hotelTotalPrice;

  const validationSchema = yup.object().shape({
    termsAndConditions: yup.boolean().required().isTrue(),
    leadTraveler: yup.string().required(),
    roomAssigments: yup
      .object()
      .shape(
        Object.fromEntries(
          booking.rooms.map((room, index) => [
            room.roomToken,
            yup.object({
              travelers: yup
                .array()
                .of(yup.string().required())
                .length(rooms[index].adults + rooms[index].children),
            }),
          ]),
        ),
      )
      .required(),
  });

  const leadTraveler = selectedTravellers.find(
    (traveller) =>
      getTravellerTypeAtTravelDate(traveller.birthDate, travelDate) === "adult",
  );

  const formik = useFormik<SummaryFormValues>({
    initialValues: {
      roomAssigments:
        booking.rooms.length === 1
          ? {
              [booking.rooms[0].roomToken]: {
                travelers: selectedTravellers.map((traveller) => traveller.id),
              },
            }
          : {},
      leadTraveler: leadTraveler?.id || "",
      termsAndConditions: false,
    },
    onSubmit: (values) => handleSubmit(values),

    validationSchema,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <HotelBookingSummary
        hotel={hotel}
        hotelBookingData={booking}
        selectedTravellers={selectedTravellers}
        form={formik}
        rooms={rooms}
        travelDate={travelDate}
      />
      <BottomDrawer
        loading={loading}
        totalPrice={
          paymentType === "bnpl" ? undefined : formatCurrency(totalPrice)
        }
        payLaterPrice={
          paymentType === "bnpl"
            ? formatCurrency(
                calculateMonthlyInstallment(
                  includedBnplFee,
                  selectedInstallments,
                ),
              )
            : undefined
        }
        buttonLabel={buttonLabel}
        onButtonClick={() => formik.handleSubmit()}
        disabled={disabledSubmit || !formik.isValid || !formik.dirty}
      />
    </form>
  );
};
